import { APIAsset } from './application';
import { Nullable } from './utils';

export type APIStakeholder = {
  id: number;
  parentId: Nullable<number>;
  categoryId: number;
  personType: string;
  fullName: string;
  personIdNumber: string;
  percentageShare?: number;
  isControllingPerson?: Nullable<boolean>;
  email: string;
  assets: APIAsset[];
  status: string;
  rejectComment: Nullable<string>;
  nationality: Nullable<string>;
  dateOfBirth: Nullable<string>;
  dateOfIncorporation: Nullable<string>;
  taxIdType: Nullable<string>;
  address: Nullable<string>;
  phoneNumber: Nullable<string>;
  jobTitle: Nullable<string>;
  governmentIdIssuedIn: Nullable<string>;
  governmentIdType: Nullable<string>;
  governmentIdNumber: Nullable<string>;
  addressCountry: Nullable<string>;
  addressState: Nullable<string>;
  addressCity: Nullable<string>;
  addressZip: Nullable<string>;
  addressLine1: Nullable<string>;
  addressLine2: Nullable<string>;
  countryOfIncorporation: Nullable<string>;
  stateOfIncorporation: Nullable<string>;
  businessStructure: Nullable<string>;
};

export type StakeholderFormValues = {
  personType: string;
  fullName: string;
  countryOfIncorporation: string;
  stateOfIncorporation: string;
  businessStructure: string;
  dateOfIncorporation: string;
  nationality: string;
  personIdNumber: string;
  percentageShare: number;
  isControllingPerson: boolean;
  taxIdType: string;
  dateOfBirth: string;
  address: string;
  email: string;
  phoneNumber: string;
  jobTitle: string;
  governmentIdIssuedIn: string;
  governmentIdType: string;
  governmentIdNumber: string;
  addressCountry: string;
  addressState: string;
  addressCity: string;
  addressZip: string;
  addressLine1: string;
  addressLine2: string;
};

export type DynamicField = {
  id: number;
  nameEs: string;
  nameEn: string;
  required: boolean;
  fieldType: string;
  defaultValue: string;
};

export type APIDocumentSession = {
  id: number;
  dynamicFieldValues: any[];
  assets: APIAsset[];
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  status: string;
  rejectedComment: Nullable<string>;
  companyDocument: number;
  notificationDate: Nullable<string>;
};

export type APIDocument = {
  id: number;
  lastSession: APIDocumentSession;
  sessionHistory: [];
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  documentCategoryDefinition: number;
  prevSessionRejectedComment: Nullable<string>;
};

export type APIDocumentCategory = {
  id: number;
  dynamicFields: DynamicField[];
  documents: APIDocument[];
  status: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
  isExtraDocument: boolean;
  nameEs: string;
  nameEn: string;
  descriptionEs: string;
  descriptionEn: string;
  multiple: boolean;
  enabled: boolean;
  group: string;
  documentType: DocumentType;
  company: number;
  templateEs: Nullable<APIAsset>;
  templateEn: Nullable<APIAsset>;
};

export type IAsset = {
  id: number;
  fileName: string;
  url: string;
  isLoading: boolean;
};

export enum DocumentType {
  SHAREHOLDER = 'SHAREHOLDER',
  LEGAL_REPRESENTATIVE = 'LEGAL_REPRESENTATIVE',
  BANK_ACCOUNT = 'BANK_ACCOUNT',
  GENERIC = 'GENERIC',
  COMPANY = 'COMPANY',
}

export enum CategoryGroup {
  FINANCES = 'FINANCES',
  BASIC_DOCUMENTS = 'BASIC_DOCUMENTS',
  COMMERCE = 'COMMERCE',
  LEGALS = 'LEGALS',
  POLICIES = 'POLICIES',
  LEGAL_REVIEWS = 'LEGAL_REVIEWS',
  MARCO_DOCUMENTS = 'MARCO_DOCUMENTS',
  BANK = 'BANK',
  OTHER = 'OTHER',
  LLC = 'LLC',
  COMPLIANCE = 'COMPLIANCE',
}

export type IDocumentCategory = {
  id: number;
  group: string;
  status: string;
  rejectReason: Nullable<string>;
  assets: IAsset[];
  name: string;
  description: string;
  isLoading: boolean;
  documentId: number;
  enabled: boolean;
  isExtraDocument: boolean;
  groupName: string;
  keyName: string;
  template: Nullable<IAsset>;
  documentType: DocumentType;
  notificationDate: Nullable<string>;
  readOnly?: boolean;
};

export type AddCompanyCategoryPayload = {
  group: string;
  nameEn: string;
  nameEs: string;
  descriptionEn: string;
  descriptionEs: string;
  documentType: DocumentType;
};

export type FundingRequestSOADocuments = { id: number; soaAssets: APIAsset[] };
